import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import GetAppIcon from '@mui/icons-material/GetApp';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import ViewListIcon from '@mui/icons-material/ViewList';
import WarningIcon from '@mui/icons-material/Warning';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button, Table } from 'src/common';
import { getPageTitle } from 'src/config/projectConfig';
import { getSecurityNavItemPath, SECURITY_ENDPOINTS_HREF } from 'src/mindtime/config/securityNav';
import { SECURITY_PAGE_FILTER_KEYS } from 'src/mindtime/constants/securityFilters';
import { useSecurityEdrToolbarReset, useSecurityPageFilters } from 'src/mindtime/hooks/useSecurityPageFilters';
import {
  useWazuhScope,
  WAZUH_DATE_RANGE_OPTIONS,
  type WazuhDateRangeOption,
} from 'src/mindtime/hooks/useWazuhScope';
import type { Column, TableData } from 'src/types/table';
import type { WazuhMitreTechniqueItem } from 'src/types/mindtime/wazuhMitre';
import {
  EDR_COLORS,
  EdrDataCard,
  EdrDataToolbar,
  EdrKpiCard,
  EdrPageHeader,
  EdrSeverityPill,
  EdrTruncatedText,
  edrMonoStyle,
  type EdrSeverityLevel,
  type EdrToolbarFilter,
} from '../shared/edrPrimitives';
import { useMitreData, useMitreExport } from './useMitreData';

const breadcrumb = {
  links: [
    { name: 'EDR', to: SECURITY_ENDPOINTS_HREF },
    { name: 'MITRE ATT&CK', to: getSecurityNavItemPath('mitre-attck') ?? '' },
  ],
};

const SEVERITY_OPTIONS = [
  { label: 'Critical', value: 'critical' },
  { label: 'High', value: 'high' },
  { label: 'Medium', value: 'medium' },
  { label: 'Low', value: 'low' },
];

const DATE_RANGE_OPTIONS = WAZUH_DATE_RANGE_OPTIONS.map((option) => ({
  label: option,
  value: option,
}));

const formatNumber = (value: number | undefined | null): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return value.toLocaleString();
};

const MitreAttck: FC = () => {
  const { dateRange, setDateRange, isReseller } = useWazuhScope();
  const { filters, setFilter, search, searchInput, setSearchInput, resetFilters } =
    useSecurityPageFilters(SECURITY_PAGE_FILTER_KEYS.mitreAttck);
  const {
    tactic = '',
    technique = '',
    severityBand = '',
    organizationId = '',
    wazuhAgentId = '',
    mitreId = '',
  } = filters;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const mitreFilters = useMemo(
    () => ({
      tactic: filters.tactic,
      technique: filters.technique,
      mitreId: filters.mitreId,
      severityBand: filters.severityBand,
      wazuhAgentId: filters.wazuhAgentId,
      organizationId: filters.organizationId,
    }),
    [filters]
  );

  const { loading, summary, items: paged, topTechniques, facets } = useMitreData(
    page,
    rowsPerPage,
    search,
    mitreFilters
  );
  const { exporting, handleExport } = useMitreExport(search, mitreFilters);

  const items = paged?.items ?? [];
  const totalRecords = paged?.totalRecords ?? 0;

  const handleResetFilters = useSecurityEdrToolbarReset(resetFilters, setDateRange, setPage);

  const toolbarFilters: EdrToolbarFilter[] = useMemo(
    () => [
      {
        id: 'tactic',
        label: 'Tactic',
        options: facets.tactics.map((name) => ({ label: name, value: name })),
        value: tactic,
        onChange: (value) => {
          setFilter('tactic', value);
          setPage(0);
        },
      },
      {
        id: 'technique',
        label: 'Technique',
        options: facets.techniques.map((name) => ({ label: name, value: name })),
        value: technique,
        onChange: (value) => {
          setFilter('technique', value);
          setPage(0);
        },
      },
      {
        id: 'severity',
        label: 'Severity',
        icon: <WarningIcon style={{ fontSize: 14 }} />,
        options: SEVERITY_OPTIONS,
        value: severityBand,
        onChange: (value) => {
          setFilter('severityBand', value);
          setPage(0);
        },
      },
      ...(isReseller
        ? [
            {
              id: 'org',
              label: 'Organization',
              variant: 'resellerClient' as const,
              value: organizationId,
              onChange: (value: string) => {
                setFilter('organizationId', value);
                setFilter('wazuhAgentId', '');
                setPage(0);
              },
            },
          ]
        : []),
      {
        id: 'endpoint',
        label: 'Endpoint',
        variant: 'searchableEndpoint' as const,
        pageOrganizationId: organizationId || undefined,
        value: wazuhAgentId,
        onChange: (value: string) => {
          setFilter('wazuhAgentId', value);
          setPage(0);
        },
      },
      {
        id: 'mitreId',
        label: 'MITRE ID',
        options: facets.mitreIds.map((id) => ({ label: id, value: id })),
        value: mitreId,
        onChange: (value) => {
          setFilter('mitreId', value);
          setPage(0);
        },
      },
      {
        id: 'date',
        label: 'Date Range',
        icon: <CalendarTodayIcon style={{ fontSize: 14 }} />,
        options: DATE_RANGE_OPTIONS,
        value: dateRange,
        onChange: (value) => {
          setDateRange(value as WazuhDateRangeOption);
          setPage(0);
        },
      },
    ],
    [
      tactic,
      technique,
      severityBand,
      isReseller,
      organizationId,
      wazuhAgentId,
      mitreId,
      dateRange,
      facets.tactics,
      facets.techniques,
      facets.mitreIds,
      setFilter,
      setDateRange,
    ]
  );

  const columns: Column[] = useMemo(
    () => [
      {
        id: 'mitreId',
        label: 'MITRE ID',
        render: (row) => (
          <span style={{ ...edrMonoStyle, fontWeight: 600, color: EDR_COLORS.brand600 }}>
            {(row as unknown as WazuhMitreTechniqueItem).mitreId || '—'}
          </span>
        ),
      },
      {
        id: 'technique',
        label: 'Technique',
        render: (row) => (
          <EdrTruncatedText
            value={(row as unknown as WazuhMitreTechniqueItem).techniqueName || '—'}
            style={{ fontWeight: 600, color: EDR_COLORS.textPrimary }}
            maxWidth={280}
          />
        ),
      },
      {
        id: 'tactic',
        label: 'Tactic',
        render: (row) => (
          <span style={{ color: EDR_COLORS.textSecondary }}>
            {(row as unknown as WazuhMitreTechniqueItem).tactic || '—'}
          </span>
        ),
      },
      {
        id: 'detections',
        label: 'Detections',
        render: (row) => (
          <span style={{ fontWeight: 600, color: EDR_COLORS.textPrimary }}>
            {formatNumber((row as unknown as WazuhMitreTechniqueItem).detections)}
          </span>
        ),
      },
      {
        id: 'endpoint',
        label: 'Affected Endpoint',
        render: (row) => (
          <EdrTruncatedText
            value={(row as unknown as WazuhMitreTechniqueItem).endpoint || '—'}
            style={edrMonoStyle}
            maxWidth={160}
          />
        ),
      },
      {
        id: 'severity',
        label: 'Severity',
        render: (row) => (
          <EdrSeverityPill
            level={(row as unknown as WazuhMitreTechniqueItem).severity as EdrSeverityLevel}
          />
        ),
      },
    ],
    []
  );

  const topTechniqueColumns: Column[] = useMemo(
    () => [
      {
        id: 'label',
        label: 'Technique',
        render: (row) => {
          const item = row as unknown as WazuhMitreTechniqueItem;
          return (
            <span style={{ fontWeight: 600, color: EDR_COLORS.textPrimary }}>
              {[item.mitreId, item.techniqueName].filter(Boolean).join(' ') || '—'}
            </span>
          );
        },
      },
      {
        id: 'count',
        label: 'Count',
        align: 'right',
        render: (row) => (
          <span style={{ fontWeight: 700, color: EDR_COLORS.textPrimary }}>
            {formatNumber((row as unknown as WazuhMitreTechniqueItem).detections)}
          </span>
        ),
      },
    ],
    []
  );

  return (
    <>
      <Helmet>
        <title>{getPageTitle('Security — MITRE ATT&CK')}</title>
      </Helmet>

      <Box
        p={3}
        display="flex"
        flexDirection="column"
        style={{ gap: 16, minHeight: '100%', boxSizing: 'border-box' }}
      >
        <EdrPageHeader
          breadcrumb={breadcrumb}
          title="MITRE ATT&CK"
          subtitle="Analyze attacker tactics and techniques detected across your protected environment."
          actions={(
            <Button
              variant="contained"
              color="primary"
              startIcon={<GetAppIcon />}
              style={{ textTransform: 'none' }}
              loading={exporting}
              onClick={handleExport}
            >
              Export
            </Button>
          )}
        />

        <Box
          display="grid"
          style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 16 }}
        >
          <EdrKpiCard
            label="Active Techniques"
            value={formatNumber(summary?.activeTechniques)}
            icon={<TrackChangesIcon style={{ fontSize: 18 }} />}
            iconColor={EDR_COLORS.brand500}
          >
            Currently observed
          </EdrKpiCard>
          <EdrKpiCard
            label="High-Risk Techniques"
            value={formatNumber(summary?.highRiskTechniques)}
            icon={<WarningIcon style={{ fontSize: 18 }} />}
            iconColor={EDR_COLORS.critical}
          >
            Require immediate investigation
          </EdrKpiCard>
        </Box>

        <EdrDataCard
          toolbar={(
            <EdrDataToolbar
              searchValue={searchInput}
              onSearchChange={(value) => {
                setSearchInput(value);
                setPage(0);
              }}
              searchPlaceholder="Search Techniques…"
              filters={toolbarFilters}
              onReset={handleResetFilters}
            />
          )}
        >
          <Table
            columns={columns}
            data={items as unknown as TableData[]}
            loading={loading}
            showPagination
            page={page}
            rowsPerPage={rowsPerPage}
            totalRows={totalRecords}
            onPageChange={setPage}
            onRowsPerPageChange={(next) => {
              setRowsPerPage(next);
              setPage(0);
            }}
            TableContainerProps={{ sx: { boxShadow: 'none', borderRadius: 0 } }}
          />
        </EdrDataCard>

        <EdrDataCard
          toolbar={(
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <ViewListIcon style={{ fontSize: 16, color: EDR_COLORS.brand500 }} />
              <Typography
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: EDR_COLORS.textPrimary,
                }}
              >
                Top Observed Techniques
              </Typography>
            </Box>
          )}
        >
          <Table
            columns={topTechniqueColumns}
            data={topTechniques as unknown as TableData[]}
            loading={false}
            showPagination={false}
            TableContainerProps={{ sx: { boxShadow: 'none', borderRadius: 0 } }}
          />
        </EdrDataCard>
      </Box>
    </>
  );
};

export default MitreAttck;
